import React, { useState } from 'react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { Button } from '@/components/ui/Button';
import { Loader2, Coins, Check, SlidersHorizontal, Ticket, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { auth } from '@/services/firebase';
import toast from 'react-hot-toast';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PACKAGES = [
  { amount: 5, label: "Starter" },
  { amount: 15, label: "Advanced" },
  { amount: 25, label: "Pro", popular: true },
  { amount: 50, label: "Premium" },
];

function getCreditsInfo(amount: number) {
  let base = amount * 20;
  let bonus = 0;
  if (amount >= 50) bonus = 300;
  else if (amount >= 25) bonus = 100;
  return { credits: base + bonus, bonus, bonusValue: bonus / 20 };
}

export function TopUpModal({ isOpen, onClose }: TopUpModalProps) {
  const [amount, setAmount] = useState<number>(25);
  const [discountCode, setDiscountCode] = useState<string>("");
  const [discountStatus, setDiscountStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [discountMessage, setDiscountMessage] = useState<string>("");
  const creditsInfo = getCreditsInfo(amount);

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountStatus('validating');
    setDiscountMessage("");
    
    try {
      const token = await auth?.currentUser?.getIdToken();
      const res = await fetch('/api/validate-discount', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: discountCode })
      });
      const data = await res.json();
      
      if (res.ok && data.valid) {
        setDiscountStatus('valid');
        setDiscountMessage(`Discount applied! ${data.discount?.description || ''}`);
      } else {
        setDiscountStatus('invalid');
        setDiscountMessage(data.error || "Invalid discount code");
      }
    } catch (err) {
      setDiscountStatus('invalid');
      setDiscountMessage("Failed to validate code");
    }
  };

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const token = await auth?.currentUser?.getIdToken();
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount, discountCode })
      });
      const data = await res.json();
      if (!data.checkoutUrl) {
        throw new Error(data.error || "Failed to initiate checkout");
      }
      return data.checkoutUrl;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to connect to checkout");
    }
  });

  return (
    <ResponsiveOverlay isOpen={isOpen} onClose={onClose} className="p-0 sm:max-w-2xl w-full flex flex-col max-h-[90vh] sm:max-h-[85vh] overflow-hidden bg-white dark:bg-slate-950">
      
      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 dark:bg-emerald-900/50 p-2.5 rounded-xl">
                <Coins className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">Buy Credits</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Top-up your balance to continue using AI tools.</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close Top-Up Dialog"
              className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PACKAGES.map((pkg) => {
              const isSelected = amount === pkg.amount;
              const pkgInfo = getCreditsInfo(pkg.amount);
              return (
                <div 
                  key={pkg.amount}
                  onClick={() => setAmount(pkg.amount)}
                  className={`relative flex flex-col p-5 rounded-2xl border transition-all cursor-pointer ${
                    isSelected 
                      ? 'border-emerald-500 ring-1 ring-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10' 
                      : 'border-slate-200 dark:border-slate-800 hover:border-emerald-200 dark:hover:border-slate-700 bg-white dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3 rounded-full shadow-sm">
                      Most Popular
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute top-4 right-4 text-emerald-500">
                      <Check className="w-5 h-5" strokeWidth={3} />
                    </div>
                  )}
                  <div className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">{pkg.label}</div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-bold text-slate-900 dark:text-white">${pkg.amount}</span>
                    {pkgInfo.bonusValue > 0 && <span className="text-sm text-slate-400 line-through">${pkg.amount + pkgInfo.bonusValue}</span>}
                  </div>
                  <div className="mt-auto flex items-center flex-wrap gap-2 font-medium">
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <Coins className="w-4 h-4" /> {pkgInfo.credits} Credits
                    </span>
                    {pkgInfo.bonus > 0 && (
                      <span className="text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                        +${pkgInfo.bonusValue} Bonus Value
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 bg-slate-50 dark:bg-slate-900/40 px-6 pt-5 pb-6 rounded-2xl border border-slate-200 dark:border-slate-800">
            <label htmlFor="amount-slider" className="flex justify-between items-center text-sm font-medium text-slate-700 dark:text-slate-300 mb-6 cursor-pointer">
              <span className="flex flex-col">
                <span className="flex items-center gap-1.5 font-semibold text-slate-900 dark:text-white"><SlidersHorizontal className="w-4 h-4 text-slate-500" /> Custom Amount</span>
                <span className="text-slate-500 text-[13px] font-normal mt-0.5">Slide to select exactly what you need</span>
              </span>
              <div className="text-right">
                <div className="text-2xl flex items-center justify-end gap-1.5 font-bold text-slate-900 dark:text-white">
                  ${amount} 
                </div>
                <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1">{creditsInfo.credits} Credits</div>
              </div>
            </label>
            
            <div className="relative w-full pt-1 pb-8 select-none">
              <div className="relative h-2.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full">
                {/* Progress fill */}
                <div className="absolute top-0 left-0 h-full bg-emerald-500 rounded-full pointer-events-none transition-all duration-150 ease-out" style={{ width: `${((amount - 5) / 45) * 100}%` }} />
                
                {/* Input overlay for dragging */}
                <input 
                  type="range" 
                  min="5" max="50" step="5"
                  value={amount} 
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="absolute -top-3 left-0 w-full h-8 opacity-0 cursor-pointer z-30"
                />

                {/* Custom Thumb */}
                <div 
                    className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white dark:bg-slate-100 border-[3px] border-emerald-500 rounded-full shadow-md z-0 pointer-events-none transition-all duration-150 ease-out"
                    style={{ left: `calc(${((amount - 5) / 45) * 100}% - 10px)` }}
                />

                {/* Marks */}
                {[5, 15, 25, 40, 50].map((mark) => {
                  const markPercent = ((mark - 5) / 45) * 100;
                  return (
                  <button
                    key={mark}
                    type="button"
                    onClick={() => setAmount(mark)}
                    className="absolute top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center -translate-x-1/2 z-20 cursor-pointer focus:outline-none group"
                    style={{ left: `${markPercent}%` }}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full transition-colors opacity-90 ${amount >= mark ? 'bg-white' : 'bg-white dark:bg-slate-600 shadow-sm'}`} />
                    <span className={`absolute top-full mt-2 text-[11px] font-bold transition-colors ${amount === mark ? 'text-emerald-600 dark:text-emerald-400 scale-110' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}>${mark}</span>
                  </button>
                )})}
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-2">
             <label htmlFor="discount" className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Have a discount code?</label>
             <div className="relative flex items-center gap-2">
               <div className="relative flex-1">
                 <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600 dark:text-emerald-500" />
                 <input 
                   id="discount"
                   type="text"
                   placeholder="e.g. EARLYBIRD20"
                   value={discountCode}
                   onChange={(e) => {
                     setDiscountCode(e.target.value.toUpperCase());
                     setDiscountStatus('idle');
                     setDiscountMessage('');
                   }}
                   className="w-full text-sm uppercase font-medium pl-9 pr-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:focus:border-emerald-500"
                 />
               </div>
               <Button 
                 variant="outline" 
                 onClick={validateDiscount}
                 disabled={!discountCode || discountStatus === 'validating'}
                 className="h-[46px] border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
               >
                 {discountStatus === 'validating' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
               </Button>
             </div>
             {discountMessage && (
               <div className={`text-xs mt-0.5 font-medium ml-1 flex items-center gap-1.5 ${discountStatus === 'valid' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                 {discountStatus === 'valid' ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                 {discountMessage}
               </div>
             )}
          </div>
        </div>
      </div>
      
      {/* Sticky Footer */}
      <div className="flex-none p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur flex w-full flex-col sm:flex-row items-center justify-end gap-3 z-30">
        <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto text-slate-600 dark:text-slate-400">
          Cancel
        </Button>
        <Button 
          onClick={() => checkoutMutation.mutate()} 
          disabled={checkoutMutation.isPending}
          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white min-w-[200px] h-11 text-[15px] shadow-sm shadow-emerald-500/20"
        >
          {checkoutMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
          ) : (
            `Continue to checkout`
          )}
        </Button>
      </div>
    </ResponsiveOverlay>
  );
}
